import { decode } from 'html-entities'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styles from './styles.module.scss'
import Badge from 'srcReact/components/common2/Badge'
import Icon from 'srcReact/components/icons/Icon'
import { getFlaggedColor } from 'srcReact/helpers/utils'

import Avatar from '../Avatar'
import SuspendText from '../SuspendText'

function GroupCard({
  g,
  i,
  id, //for general group block
  handleClick,
  getGroupRoleColor,
  infiniteScrollRef,
  showUserModal,
  flaggedList,
  isSuspended,
  groupTitleClassName,
  className
}) {
  const { t } = useTranslation()
  const isFlagged = flaggedList?.length >= 1 && flaggedList.find((f) => f.id_group === g.group_id)

  return (
    <React.Fragment>
      {g.is_general_group == 1 ? (
        <a
          role='button'
          tabIndex='0'
          id={id}
          aria-label={t('myhome.mygroups.groupDescription', {
            groupName: g.group_title,
            count: g.number_of_members,
            role: g.role,
            visibility: t('groupHome.header.general'),
            extraInfo: isSuspended ? t('moderationReport.suspend.objSuspended', { object: 'group' }) : '',
            userName: `${g.organizer_firstname || g.author_firstname} ${g.organizer_lastname || g.author_lastname}`
          })}
          className={`${styles.cardItem} ${styles['defaultImage']} ${
            isSuspended && g.role === 'member' && styles.disabledCardItem
          } ${className}`}
          style={
            g.group_cover || g.group_picture
              ? {
                  backgroundImage: `url(${g.group_cover || g.group_picture})`
                }
              : null
          }
          onClick={() => {
            if (handleClick) {
              handleClick(g)
            }
          }}
          ref={infiniteScrollRef}>
          <div className={styles.overlay} />
          {g.role && getGroupRoleColor && (
            <div className={`${styles.role} ${styles[getGroupRoleColor(g.role)]}`}>
              {t(`components.avatar.${g.role}`)}
            </div>
          )}

          <div className={`${styles.title} ${groupTitleClassName}`}>{decode(g.group_title || g.group_name)}</div>
          <div className={styles.general}>
            <div className={`${styles.workplaceName} ${styles[g.workplace.color]}`}>{g.workplace.name}</div>
            <div className={styles.generalGroupText}>{t('myhome.mygroups.generalGroup')}</div>
            <div className={`${styles.backgroundImage} ${styles[g.workplace.color]}`} />
          </div>
          {isSuspended ? (
            <SuspendText
              className={styles.suspendedGroup}
              text={t('moderationReport.suspend.objSuspended', { object: 'group' })}
            />
          ) : (
            isFlagged &&
            isFlagged.flag_count >= 1 && (
              <div className={styles.flagBadge}>
                <Badge
                  size='large'
                  color={getFlaggedColor(isFlagged.flag_count)}
                  icon='flag-icon'
                  text={isFlagged.flag_count}
                />
              </div>
            )
          )}
          {!isSuspended && g.notifications >= 1 && (
            <div className={styles.badge}>
              <Badge size='medium' color='green' text={g.notifications} />
            </div>
          )}
        </a>
      ) : (
        <button
          aria-label={t('myhome.mygroups.groupDescription', {
            groupName: g.group_title,
            count: g.number_of_members,
            role: g.role,
            visibility: t(`groupHome.header.${g.group_visibility}`),
            extraInfo: isSuspended ? t('moderationReport.suspend.objSuspended', { object: 'group' }) : '',
            userName: `${g.organizer_firstname || g.author_firstname} ${g.organizer_lastname || g.author_lastname}`
          })}
          className={`${styles.cardItem} ${styles['defaultImage']} ${
            isSuspended && g.role === 'member' && styles.disabledCardItem
          } ${className}`}
          style={
            g.group_cover || g.group_picture
              ? {
                  backgroundImage: `url(${g.group_cover || g.group_picture})`
                }
              : null
          }
          onClick={() => {
            if (handleClick) {
              handleClick(g)
            }
          }}
          ref={infiniteScrollRef}>
          <div className={styles.overlay} />
          {g.role && getGroupRoleColor && (
            <div className={`${styles.role} ${styles[getGroupRoleColor(g.role)]}`}>
              {t(`components.avatar.${g.role}`)}
            </div>
          )}

          <div className={`${styles.title} ${groupTitleClassName}`}>{decode(g.group_title || g.group_name)}</div>
          {g.group_type !== 'expertise' && (
            <button
              className={`${styles.organizerInfo}`}
              onClick={(e) => {
                if (showUserModal) {
                  showUserModal(e, g)
                }
              }}>
              <Avatar
                ariaLabel={t('profile_light.arialabel', {
                  userName: g.organizer_firstname || g.author_firstname
                })}
                imgWrapperSize={'small43'}
                userId={g.organizer_id || g.post_author}
                profilePicture={g.organizer_picture}
                firstname={g.organizer_firstname || g.author_firstname}
                lastname={g.organizer_lastname || g.author_lastname}
                mimeType={g.mime_type}
                originalTag={g.original_tag}
                isOriginal={g.is_original}
                containerCSS={styles.image}
                withExpertiseGroup={g.with_expertise_group}
                showImageAsDecorative={true}
                onClickAction={(e) => {
                  if (showUserModal) {
                    showUserModal(e, g)
                  }
                }}
              />

              <div className={styles.nameAndCount}>
                <div className={styles.name}>{g.organizer_name || `${g.author_firstname} ${g.author_lastname}`}</div>
                <span className={styles.members}>
                  {g.number_of_members} {t('myhome.mygroups.members')}
                </span>
              </div>

              {g.group_visibility && g.group_visibility !== 'public' && (
                <Icon name='mask' styleModifier={styles.maskIcon} />
              )}
            </button>
          )}
          {g.group_type === 'expertise' && (
            <div className={styles.expertiseGroup}>
              <Icon styleModifier={styles.icon} name='star-solid' size='large24' />
              <div className={`${styles.backgroundImage} ${styles.purple}`} />
            </div>
          )}

          {isSuspended ? (
            <SuspendText
              className={styles.suspendedGroup}
              text={t('moderationReport.suspend.objSuspended', { object: 'group' })}
            />
          ) : (
            isFlagged &&
            isFlagged.flag_count >= 1 && (
              <div className={styles.flagBadge}>
                <Badge
                  size='large'
                  color={getFlaggedColor(isFlagged.flag_count)}
                  icon='flag-icon'
                  text={isFlagged.flag_count}
                />
              </div>
            )
          )}

          {!isSuspended && g.notifications >= 1 && (
            <div className={styles.badge}>
              <Badge size='medium' color='green' text={g.notifications} />
            </div>
          )}
        </button>
      )}
    </React.Fragment>
  )
}

export default GroupCard
